import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Avatar,
  styled,
  TextField,
  Paper,
  Tooltip,
  CircularProgress,
  Fade,
  Chip,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChatBubble as ChatBubbleIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  Translate as TranslateIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

const ChatWindow = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: '80px',
  right: '20px',
  width: '380px',
  height: '600px',
  backgroundColor: '#1a1a1a',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  border: '1px solid #ffd700',
}));

const FloatingChatButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  backgroundColor: '#ffd700',
  color: '#000000',
  width: '56px',
  height: '56px',
  '&:hover': {
    backgroundColor: '#ffed4a',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
}));

const MessageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: '20px',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' ? '#2d2d2d' : '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' ? '#555' : '#888',
    borderRadius: '4px',
  },
}));

const MessageBubble = styled(Box)(({ theme, isAi }) => ({
  maxWidth: '80%',
  padding: '12px 16px',
  borderRadius: isAi ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
  backgroundColor: isAi 
    ? '#2d2d2d'
    : '#ffd700',
  color: isAi 
    ? '#ffffff'
    : '#000000',
  marginBottom: '8px',
  position: 'relative',
  '&:hover': {
    '& .message-actions': {
      opacity: 1,
    },
  },
}));

const Chat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messageEndRef = useRef(null);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const languages = {
    en: { name: 'English', welcome: "Hello! I'm your AI assistant. How can I help you today?" },
    es: { name: 'Español', welcome: "¡Hola! Soy tu asistente AI. ¿Cómo puedo ayudarte hoy?" },
    fr: { name: 'Français', welcome: "Bonjour! Je suis votre assistant AI. Comment puis-je vous aider aujourd'hui?" },
  };

  const handleLanguageChange = (lang) => {
    setCurrentLanguage(lang);
    setMessages([{
      id: Date.now(),
      type: 'ai',
      content: languages[lang].welcome,
      timestamp: new Date(),
    }]);
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    // You could add a toast notification here
  };

  const handleDelete = (messageId) => {
    setMessages(messages.filter(msg => msg.id !== messageId));
  };

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 1,
        type: 'ai',
        content: languages[currentLanguage].welcome,
        timestamp: new Date(),
      }]);
    }
  }, [isOpen, currentLanguage]);

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        content: 'This is a simulated AI response. You can integrate with your actual AI service here.',
        timestamp: new Date(),
      }]);
    }, 1500);
  };

  return (
    <Container maxWidth="xl">
      <FloatingChatButton
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Chat with AI"
      >
        <Fade in={true}>
          {isOpen ? <CloseIcon /> : <ChatBubbleIcon />}
        </Fade>
      </FloatingChatButton>

      {isOpen && (
        <ChatWindow elevation={3}>
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #ffd700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#000000',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#ffd700', color: '#000000' }}>AI</Avatar>
              <Typography variant="h6" sx={{ color: '#ffffff' }}>AI Assistant</Typography>
            </Box>
            <Box>
              <Tooltip title="Change Language">
                <IconButton sx={{ color: '#ffd700' }} onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <TranslateIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Settings">
                <IconButton sx={{ color: '#ffd700' }}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Messages */}
          <MessageContainer sx={{ 
            backgroundColor: '#1a1a1a',
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#2d2d2d',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ffd700',
            },
          }}>
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                {msg.type === 'ai' && (
                  <Avatar
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      mr: 1,
                      bgcolor: 'primary.main',
                    }}
                  >
                    AI
                  </Avatar>
                )}
                <MessageBubble isAi={msg.type === 'ai'}>
                  <Typography>{msg.content}</Typography>
                  <Box sx={{ 
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    opacity: 0.7,
                    fontSize: '0.75rem',
                  }}>
                    <Typography variant="caption">
                      {msg.timestamp.toLocaleTimeString()}
                    </Typography>
                    <Box className="message-actions" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
                      <Tooltip title="Copy">
                        <IconButton size="small" onClick={() => handleCopy(msg.content)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(msg.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </MessageBubble>
              </Box>
            ))}
            {isTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>AI</Avatar>
                <Chip
                  label="AI is typing..."
                  size="small"
                  icon={<CircularProgress size={16} />}
                />
              </Box>
            )}
            <div ref={messageEndRef} />
          </MessageContainer>

          {/* Input */}
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid #ffd700',
            backgroundColor: '#000000',
          }}>
            <form onSubmit={handleSend}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Add emoji">
                  <IconButton size="small" sx={{ color: '#ffd700' }}>
                    <EmojiIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Attach file">
                  <IconButton size="small" component="label" sx={{ color: '#ffd700' }}>
                    <input type="file" hidden />
                    <AttachFileIcon />
                  </IconButton>
                </Tooltip>
                <TextField
                  fullWidth
                  size="small"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message..."
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      backgroundColor: '#2d2d2d',
                      color: '#ffffff',
                      '& fieldset': {
                        borderColor: '#ffd700',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ffed4a',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ffd700',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#888888',
                    },
                  }}
                />
                <Tooltip title="Send message">
                  <IconButton 
                    type="submit" 
                    sx={{
                      backgroundColor: '#ffd700',
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: '#ffed4a',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#2d2d2d',
                        color: '#666666',
                      },
                    }}
                    disabled={!message.trim() || isTyping}
                  >
                    <SendIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </form>
          </Box>
        </ChatWindow>
      )}

      {/* Language Menu with updated styling */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a1a',
            border: '1px solid #ffd700',
            '& .MuiMenuItem-root': {
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#2d2d2d',
              },
              '&.Mui-selected': {
                backgroundColor: '#ffd700',
                color: '#000000',
              },
            },
          },
        }}
      >
        {Object.entries(languages).map(([code, lang]) => (
          <MenuItem 
            key={code}
            onClick={() => {
              handleLanguageChange(code);
              setAnchorEl(null);
            }}
            selected={currentLanguage === code}
          >
            {lang.name}
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
};

export default Chat;

